import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { LocalTicket } from '../../db/LocalTicket.js';
import { renderStructuredDescription } from './ticket-description.js';
// 🆕 2026-08-29 — Cost Analytics hidden on request. Flip to true to bring the tab back; the
// content dispatch below is guarded by the same flag, so the page can never open on a tab whose
// button no longer exists.
const SHOW_COST_ANALYTICS = false;

// 🆕 2026-05-26 — Cost Analytics sub-tab (CallSession-driven, separate from per-ticket view).
import { CostAnalytics } from './CostAnalytics.js';

type SubTab = 'tickets' | 'costs';

export const LocalTicketOverview = stateful(() => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SubTab>('tickets');

  const dataViewOptions = buildFullOptions({
    class: LocalTicket,
    isSortable: false,
    customColumns: [
      { fieldName: 'ticketNumber', columnName: 'Ticket ID', columnWidth: '150px' },
      { fieldName: 'phoneNumber', columnName: 'Telefonnummer', columnWidth: '200px' },
      { fieldName: 'accountName', columnName: 'Firma', columnWidth: '200px' },
      {
        fieldName: 'incompleteCapture',
        columnName: '⚠',
        columnWidth: '60px',
        renderItem: ticket =>
          ticket.incompleteCapture
            ? html`<span class="badge badge-warning" title="Unvollständige Erfassung">?</span>`
            : '',
      },
      { fieldName: 'dossierNumber', columnName: 'Dossier Nr.', columnWidth: '150px' },
      {
        fieldName: 'description',
        columnName: 'Problem',
        renderItem: ticket => renderStructuredDescription(ticket.description, { class: 'text-sm leading-snug' }),
      },
      { fieldName: 'status', columnName: 'Status', columnWidth: '120px' },
      {
        fieldName: 'crmStatus',
        columnName: 'CRM-Bestätigung',
        columnWidth: '170px',
        renderItem: ticket =>
          ticket.crmStatus === 'confirmed'
            ? 'Bestätigt'
            : html`<span class="badge badge-warning">Manuell prüfen</span>`,
      },
      { fieldName: 'queueStatus', columnName: 'Zuweisung', columnWidth: '140px' },
      { fieldName: 'priority', columnName: 'Priorität', columnWidth: '100px' },
      {
        fieldName: 'createdOn',
        columnName: 'Erstellt am',
        columnWidth: '175px',
        renderItem: ticket => (ticket.createdOn ? format(new Date(ticket.createdOn), 'yyyy-MM-dd HH:mm') : ''),
      },
      {
        customID: '_options',
        columnWidth: '125px',
        renderItem: ticket => html`
          <a class="btn btn-primary btn-sm" href=${`/local-tickets/ticket/${ticket._id}`}>Go to details</a>
        `,
      },
    ],
    view: new View({
      filter: new AdornisFilter({
        sortingFields: ['createdOn'],
        sortingDirections: ['desc'],
        search: searchQuery || null,
        searchFields: ['ticketNumber', 'phoneNumber', 'accountName', 'accountNumber', 'dossierNumber'],
      }),
    }),
  });

  // ─── Tab switcher (Tickets vs Cost Analytics) ───────────────────
  const tabBar = html`
    <div role="tablist" class="tabs tabs-boxed mx-4 mt-4 mb-0 inline-flex w-fit">
      <button
        role="tab"
        class="tab ${activeTab === 'tickets' ? 'tab-active' : ''}"
        @click=${() => setActiveTab('tickets')}
      >
        Ticket Übersicht
      </button>
      ${SHOW_COST_ANALYTICS
        ? html`<button
            role="tab"
            class="tab ${activeTab === 'costs' ? 'tab-active' : ''}"
            @click=${() => setActiveTab('costs')}
          >
            Cost Analytics
          </button>`
        : nothing}
    </div>
  `;

  // ─── Tickets tab content (unchanged from before) ────────────────
  const ticketsTabContent = html`
    <div class="card bg-base-100 m-4">
      <div class="card-body p-8">
        <div class="flex flex-row justify-between space-x-4">
          <h2 class="card-title text-2xl font-bold text-primary mb-6">Ticket Übersicht</h2>
          <div class="flex items-center justify-between">
            <label class="input input-bordered flex items-center gap-2 min-w-xl">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 opacity-70 flex-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                />
              </svg>
              <input
                type="text"
                class="grow"
                placeholder="Search tickets…"
                @input=${e => setSearchQuery((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>
        </div>
        <div>${DataTable({ options: dataViewOptions.dataViewOptions })}</div>
      </div>
    </div>
  `;

  return html` ${tabBar} ${activeTab === 'costs' && SHOW_COST_ANALYTICS ? CostAnalytics() : ticketsTabContent} `;
});
